import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumbs from '../../components/Breadcrumbs';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, CheckCircle, FileText, Clock } from 'lucide-react';

const LicenseExceptionVsValidated = () => {
  const comparisonData = [
    {
      category: "Authorization Level",
      exception: "Pre-approved authorization meeting specific conditions",
      validated: "Individual authorization for specific transaction"
    },
    {
      category: "Application Required",
      exception: "No application needed - self-determined eligibility",
      validated: "Formal application required to BIS or DDTC"
    },
    {
      category: "Processing Time",
      exception: "Immediate (if conditions met)",
      validated: "4-8 weeks (BIS) or 60-90+ days (DDTC)"
    },
    {
      category: "Government Review",
      exception: "No advance government review",
      validated: "Detailed government review and approval"
    },
    {
      category: "Scope of Authorization",
      exception: "Broader authorization for qualifying transactions",
      validated: "Specific items, quantities, parties, and end-uses"
    },
    {
      category: "Compliance Burden",
      exception: "Must verify and document conditions are met",
      validated: "Must comply with license terms and conditions"
    },
    {
      category: "Common Examples (EAR)",
      exception: "TMP (temporary exports), LVS (limited value), TSU (technology/software unrestricted)",
      validated: "Required when no exception applies or for restricted destinations"
    },
    {
      category: "Cost",
      exception: "No government fees",
      validated: "$250-$3,000+ depending on agency and license type"
    },
    {
      category: "Flexibility",
      exception: "More flexible for routine transactions",
      validated: "Less flexible - bound by specific license terms"
    },
    {
      category: "Risk Level",
      exception: "Higher compliance risk if conditions misunderstood",
      validated: "Lower risk - explicit government approval obtained"
    }
  ];

  return (
    <div className="comparison-page">
      <SEO 
        title="License Exception vs Validated License: Export Authorization Guide"
        description="Understand the difference between license exceptions and validated export licenses. Learn when to use each authorization type for EAR-controlled exports."
        keywords="license exception, validated license, export authorization, EAR licensing, BIS license, export controls"
        path="/license-exception-vs-validated"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Resources', path: '/resources' },
        { label: 'License Exception vs Validated', path: '/license-exception-vs-validated' }
      ]} />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              License Exception vs Validated License
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Understanding when you can use a license exception versus when you need a validated license is crucial for efficient export compliance. This guide explains both authorization types under the Export Administration Regulations (EAR).
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="h-8 w-8 text-green-600" />
                <h3 className="text-2xl font-bold text-green-900">License Exception</h3>
              </div>
              <p className="text-green-800 mb-4 font-semibold">Pre-approved authorization for qualifying exports</p>
              <ul className="space-y-2 text-green-900">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>No application required</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Immediate if conditions met</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>No government fees</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Self-determined eligibility</span>
                </li>
              </ul>
            </div>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="h-8 w-8 text-blue-600" />
                <h3 className="text-2xl font-bold text-blue-900">Validated License</h3>
              </div>
              <p className="text-blue-800 mb-4 font-semibold">Individual authorization from BIS</p>
              <ul className="space-y-2 text-blue-900">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Formal application required</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>4-8 week processing time</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Government review and approval</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Explicit authorization</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Detailed Comparison
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="text-left p-4 font-bold text-lg">Category</th>
                  <th className="text-left p-4 font-bold text-lg">License Exception</th>
                  <th className="text-left p-4 font-bold text-lg">Validated License</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-4 font-semibold text-gray-900 border-b border-gray-200">
                      {row.category}
                    </td>
                    <td className="p-4 text-gray-700 border-b border-gray-200">
                      {row.exception}
                    </td>
                    <td className="p-4 text-gray-700 border-b border-gray-200">
                      {row.validated}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Common License Exceptions (EAR)
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">TMP - Temporary Exports</h3>
              <p className="text-gray-700 mb-2">For items exported temporarily and returned to the U.S.</p>
              <p className="text-sm text-gray-600">Common for: Trade shows, demonstrations, testing</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">LVS - Limited Value Shipments</h3>
              <p className="text-gray-700 mb-2">For exports not exceeding $5,000 to most destinations</p>
              <p className="text-sm text-gray-600">Limitations: Not for all ECCNs or destinations</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">TSU - Technology/Software Unrestricted</h3>
              <p className="text-gray-700 mb-2">For publicly available technology and software</p>
              <p className="text-sm text-gray-600">Commonly used for: Open source, published research</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">ENC - Encryption Items</h3>
              <p className="text-gray-700 mb-2">For certain encryption items after notification</p>
              <p className="text-sm text-gray-600">Required: 30-day review for initial exports</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Help Determining License Requirements?
          </h2>
          <p className="text-xl text-slate-100 mb-10 max-w-2xl mx-auto">
            Our export control specialists can help you identify available license exceptions and navigate the validated license application process.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule Export Controls Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LicenseExceptionVsValidated;